/**
 * Tree traversal and structure building
 * Functions for converting between flat and hierarchical representations
 */

import { TodoTree, TodoItem, findRootNodeId } from '../../types/todo';
import { getItemById } from './queries';

/**
 * Represents a hierarchical tree node with children
 */
export interface TreeNode {
  id: string;
  item: TodoItem;
  children: TreeNode[];
  depth: number;
}

/**
 * Builds a hierarchical tree structure from flat todo data
 * Recursively constructs TreeNode objects with proper parent-child relationships
 * @param tree - Flat todo tree structure
 * @param rootId - Optional root node ID
 * @returns Root TreeNode or null if tree is invalid
 */
export function buildTreeStructure(tree: TodoTree, rootId?: string): TreeNode | null {
  const actualRootId = rootId || findRootNodeId(tree);
  if (!actualRootId) {
    return null;
  }

  function buildNode(id: string, depth: number = 0): TreeNode | null {
    const item = getItemById(tree, id);
    if (!item) {
      return null;
    }

    const children = item.children
      .map(childId => buildNode(childId, depth + 1))
      .filter((child): child is TreeNode => child !== null);

    return {
      id,
      item,
      children,
      depth,
    };
  }

  return buildNode(actualRootId);
}

/**
 * Flattens a tree structure into an array with depth information
 * @param tree - The todo tree structure
 * @param rootId - Optional root node ID
 * @returns Array of items with their IDs and depths
 */
export function flattenTree(tree: TodoTree, rootId?: string): Array<{ id: string; item: TodoItem; depth: number }> {
  const root = buildTreeStructure(tree, rootId);
  if (!root) {
    return [];
  }

  const result: Array<{ id: string; item: TodoItem; depth: number }> = [];

  function traverse(node: TreeNode) {
    result.push({
      id: node.id,
      item: node.item,
      depth: node.depth,
    });

    node.children.forEach(traverse);
  }

  traverse(root);
  return result;
}

/**
 * Gets the maximum depth of the tree
 * @param tree - The todo tree structure
 * @param rootId - Optional root node ID
 * @returns Maximum depth level
 */
export function getMaxDepth(tree: TodoTree, rootId?: string): number {
  const root = buildTreeStructure(tree, rootId);
  if (!root) {
    return 0;
  }

  function getDepth(node: TreeNode): number {
    if (node.children.length === 0) {
      return node.depth;
    }

    return Math.max(...node.children.map(getDepth));
  }

  return getDepth(root);
}
